import React, { useState } from 'react';
import { EmailBlock, TextBlockContent } from '../types';
import { splitTextWithMergeTags, MERGE_TAGS } from '../utils/mergeTagHelper';

interface TextBlockProps {
  block: EmailBlock;
  isSelected: boolean;
  onSelect: () => void;
  isDarkMode: boolean;
}

export const TextBlock: React.FC<TextBlockProps> = ({
  block,
  isSelected,
  onSelect,
  isDarkMode
}) => {
  const content = block.content as TextBlockContent;
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(content.text);

  const handleDoubleClick = () => {
    if (isSelected) {
      setIsEditing(true);
      setEditText(content.text);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    // Update would be handled by parent component
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditText(content.text);
    }
  };

  const style: React.CSSProperties = {
    fontSize: content.fontSize || 16,
    fontFamily: content.fontFamily || 'Arial, sans-serif',
    fontWeight: content.fontWeight || 'normal',
    fontStyle: content.fontStyle || 'normal',
    color: content.color || '#000000',
    backgroundColor: content.backgroundColor || 'transparent',
    textAlign: content.textAlign || 'left',
    lineHeight: content.lineHeight || 1.5,
    padding: content.padding || '10px',
    margin: content.margin || '0',
    ...block.styles
  };

  const renderContent = () => {
    if (!content.text) return null;
    
    const parts = splitTextWithMergeTags(content.text);
    return parts.map((part, index) => {
      if (part.type === 'text') {
        // Split by newlines and render with <br/>
        const lines = part.content.split('\n');
        return lines.map((line, lineIndex) => (
          <React.Fragment key={`text-${index}-${lineIndex}`}>
            {line}
            {lineIndex < lines.length - 1 && <br />}
          </React.Fragment>
        ));
      } else {
        // Render badge for merge tag
        const tag = MERGE_TAGS.find(t => t.value === part.content);
        const displayName = tag ? tag.name : part.tagName || part.content;
        return (
          <span
            key={`badge-${index}`}
            className={`
              inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ml-1 mr-1
              ${isDarkMode
                ? 'bg-accent-900/30 text-accent-300 border border-accent-700/50'
                : 'bg-accent-100 text-accent-800 border border-accent-200'
              }
            `}
            title={tag ? `Sample: ${tag.sample}` : ''}
          >
            {displayName}
          </span>
        );
      }
    });
  };

  if (isEditing && isSelected) {
    return (
      <div style={style} className="min-h-[50px]">
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full h-full resize-none border-none outline-none bg-transparent"
          style={style}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div
      style={style}
      onDoubleClick={handleDoubleClick}
      className="min-h-[50px] cursor-text"
    >
      {renderContent()}
    </div>
  );
};
